import { generateID, spErrorTags } from '@eventr/common'
import {
    ColoursObj,
    MainButton,
    TextInputField,
    Tick,
    Typography,
    VerticalExpander,
} from '@eventr/common-storybook'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Radio,
    RadioGroup,
    Stack,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import z from 'zod'
import { DataAPI, IGeneralInfo } from '../../data/Data'
import { UserAPI } from '../../data/User'
import { NavigationView } from '../../feat/layout/NavigationView'
import i18n from '../../i18n/i18next'
import { loadingSignal } from '../../state/overlayState/overlaySignals'
import { useRouteGuardContext } from '../../state/routeGuardProvider'

import QRCode from 'react-qr-code'
import { useShallow } from 'zustand/react/shallow'
import { useDeviceInfo } from '../../feat/appState/deviceInfo'
import { bs, useBoundStore } from '../../state/bound.store'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { Utils, copyToClipboard } from '../../utils'

enum SharingPreference {
    all = 'all',
    some = 'some',
    none = 'none',
}

const inviteSchema = z.object({
    emails: z.array(
        z.string().email({
            message: 'A valid email address is required.',
        })
    ),
    sharingPreference: z.nativeEnum(SharingPreference),
    textToBeParsed: z.string(),
})

type Invite = z.infer<typeof inviteSchema>

export function AddTeamMember() {
    const {
        orgConfig,
        wpUser,
        users,
        getEventInfoV2,
        sections,
        selectedItinerary,
        selectedItinID,
    } = useBoundStore(
        useShallow((s) => {
            return {
                selectedItinerary: s.selectedItinerary,
                orgConfig: s.orgConfig,
                wpUser: s.wpUser,
                users: s.users,
                getEventInfoV2: s.getEventInfoV2,
                sections: s.sections,
                selectedItinID: s.selectedItinID,
            }
        })
    )
    const { version } = useDeviceInfo(
        useShallow((s) => {
            return { version: s.version }
        })
    )
    const { isAllowed } = useRouteGuardContext()
    const [itinSharecode, setItinSharecode] = useState<string>('')
    const [showQRCode, setShowQRCode] = useState<boolean>(true)
    const [sectionsToUpdate, setSectionsToUpdate] = useState<number[]>([])

    const initialInvite: Invite = {
        emails: [],
        sharingPreference: SharingPreference.all,
        textToBeParsed: '',
    }
    const [invite, setInvite] = useState<Invite>(initialInvite)

    useEffect(() => {
        setShowQRCode(false)
    }, [invite.sharingPreference, sectionsToUpdate])

    const getShareCode = useCallback(
        async (
            currentSharingPreference: string | undefined,
            sectionsToUpdate: number[] | undefined
        ) => {
            const shareCode = await UserAPI.getShareLink(
                selectedItinID,
                wpUser?.id ?? -1,
                false,
                currentSharingPreference as SharingPreference | undefined,
                sectionsToUpdate
            )

            setItinSharecode(shareCode.identifier)
            return shareCode.identifier
        },
        [selectedItinID, wpUser?.id]
    )

    const [lastEmailSent, setLastEmailSent] = useState<string | undefined>(
        undefined
    )

    enum ErrorMessageKey {
        email = 'email',
    }

    const [errorMessages, setErrorMessages] = useState<Record<string, string>>(
        {}
    )

    /// Catch if bad data.
    const EventTitle = () => {
        try {
            return getEventInfoV2()?.data?.title || ''
        } catch {
            return ''
        }
    }

    function validate(): boolean {
        const newErrorMessages: Record<string, string> = {}

        const result = inviteSchema.safeParse(invite)

        if (result.success === false) {
            result.error.issues.forEach((issue) => {
                if (typeof issue.path[0] === 'string') {
                    newErrorMessages[issue.path[0]] = issue.message
                }
            })
        }

        setErrorMessages(newErrorMessages)

        return Object.keys(newErrorMessages).length === 0
    }

    async function addUserToItinerary(id: number): Promise<boolean> {
        try {
            var eventGeneralInfo = getEventInfoV2()

            /// check if user already in general info

            if (
                eventGeneralInfo?.data?.members.find(
                    (optionMember: { option: number }) => {
                        return optionMember.option === id
                    }
                )
            ) {
                /// Found user, already exist
                return false
            }

            const userObj = {
                option: id,
                type: 'User',
                _id: generateID(selectedItinID, 1),
            }

            if (eventGeneralInfo && eventGeneralInfo.data) {
                eventGeneralInfo.data.members =
                    eventGeneralInfo.data.members || []

                eventGeneralInfo.data.members.push(userObj)
            }

            const generalInfo: IGeneralInfo = {
                itinerary_id: selectedItinID,
                section_id: 1,
                value: eventGeneralInfo?.data ?? {},
            }

            await DataAPI.createUpdateGeneralInfo(generalInfo)

            return true
        } catch {
            /// Failed.
            spErrorTags('Failed to add user to intinerary', ['ADD_MEMBER'])
            return false
        }
    }

    const handleOnClick = async (event: { preventDefault: () => void }) => {
        Utils.hapticFeedback('light')
        event.preventDefault()
        if (showQRCode) {
            await copyToClipboard(itinSharecode, version)
        } else {
            const x = await getShareCode(
                invite.sharingPreference,
                sectionsToUpdate
            )
            await copyToClipboard(x, version)
            setShowQRCode(true)
        }
    }

    const linkText = showQRCode
        ? i18n.t('edit-members.adduser.link-text-copy')
        : i18n.t('edit-members.adduser.link-text-make')

    async function sendInvite() {
        loadingSignal.value = true

        /// Check if user is allowed to invite
        if (!isAllowed(['allowedInviteUsers'])) {
            /// Not allowed... button should have been invisiable...
            spErrorTags(
                'Add user button should not be able to be pressed if not allowed to invite users.',
                ['ADD_MEMBER']
            )
            return
        }

        ///Check if user already in WP.
        const checkUser = users.find((user) => {
            return invite.emails.includes(user.email)
        })

        var sectionsToInclude: number[] = sectionsToUpdate

        switch (invite.sharingPreference) {
            case SharingPreference.all:
                let sectionIds: number[] = []
                sections.slice(1).forEach((section) => {
                    sectionIds.push(section.id)
                })
                sectionsToInclude = sectionIds
                break
            case SharingPreference.none:
                sectionsToInclude = []
                break
        }

        /// Deal with user already in wp. Just add to itinerary.
        if (checkUser && checkUser.id) {
            const userInItinerary =
                selectedItinerary?.v2?.[0]?.data?.members?.find(
                    (member: { option: number }) => {
                        return member.option === checkUser.id
                    }
                )
            if (userInItinerary) {
                /// User already in itinerary
                defaultAddSnack(
                    i18n.t('edit-members.adduser.already-added'),
                    'error'
                )
                loadingSignal.value = false
                return
            }

            UserAPI.addUserToItinerary({
                itineraryId: selectedItinID,
                newUserId: checkUser.id,
                currentUserId: wpUser?.id ?? -1,
                sections: sectionsToInclude,
            })
                .then((res) => {
                    if (res.status === 'error') {
                        const newErrorMessages: Record<string, string> = {}
                        newErrorMessages[ErrorMessageKey.email.toString()] =
                            res.message || i18n.t('edit-members.adduser.check')
                        setErrorMessages(newErrorMessages)
                    }
                    if (res.status === 'success') {
                        setErrorMessages({})
                        try {
                            /// ID returned in object (php array object) as id.
                            const newUserId = res.id
                            if (newUserId) {
                                addUserToItinerary(newUserId)
                            }
                            setInvite(initialInvite)
                        } catch (error) {
                            spErrorTags(
                                i18n.t('edit-members.adduser.error-adding'),
                                ['ADD_MEMBER'],
                                error
                            )
                        }
                    }
                })
                .finally(() => {
                    loadingSignal.value = false
                    defaultAddSnack(
                        i18n.t('edit-members.adduser.added'),
                        'success'
                    )
                })

            // loadingSignal.value = false
            return
        }

        /// Not already in wp.
        const eventTitle = EventTitle()

        UserAPI.sendInviteNew({
            eventTitle: eventTitle,
            users: [
                ...invite.emails.map((email) => {
                    return { email: email.toLowerCase() }
                }),
            ],
            sections: sectionsToInclude,
            itineraryId: selectedItinID,
            userID: wpUser?.id,
        })
            .then((res) => {
                if (res[0].status === 'error') {
                    const newErrorMessages: Record<string, string> = {}
                    newErrorMessages[ErrorMessageKey.email.toString()] =
                        res[0].message || i18n.t('edit-members.adduser.check')
                    setErrorMessages(newErrorMessages)

                    if (res[0].message === 'Email already exists') {
                        defaultAddSnack(
                            i18n.t('edit-members.adduser.already-added'),
                            'error'
                        )
                    } else {
                        defaultAddSnack(
                            i18n.t('edit-members.adduser.error-inviting'),
                            'error'
                        )
                    }
                }
                if (res[0].status === 'success') {
                    setLastEmailSent(invite.emails[0])
                    setInvite(initialInvite)

                    bs().setOrgConfig({
                        ...orgConfig,
                        isRocketChatInitialised: true,
                    })
                    setErrorMessages({})
                    try {
                        /// ID returned in object (php array object) as id.
                        const newUserId = res[0].id
                        if (newUserId) {
                            addUserToItinerary(newUserId)
                        }
                    } catch (error) {
                        spErrorTags(
                            'Error adding user to itinerary',
                            ['ADD_MEMBER'],
                            error
                        )
                    }
                }
            })
            .finally(() => {
                loadingSignal.value = false
                defaultAddSnack(
                    i18n.t('edit-members.adduser.invitesSent'),
                    'success'
                )
            })
    }

    const sharingPreferencePicker = (
        <>
            <Stack gap="16px">
                <Box>
                    <Typography color={ColoursObj.core.primary}>
                        {i18n.t('edit-members.adduser.will-add-to', {
                            eventName: EventTitle(),
                            interpolation: { escapeValue: false },
                        })}
                    </Typography>
                </Box>

                <RadioGroup
                    value={invite.sharingPreference}
                    onChange={(event) => {
                        setInvite({
                            ...invite,
                            sharingPreference: (
                                event.target as HTMLInputElement
                            ).value as SharingPreference,
                        })
                    }}
                >
                    <FormControlLabel
                        id="travelwithMeOption"
                        value={SharingPreference.all}
                        control={<Radio />}
                        label={
                            <Stack>
                                <Typography color={ColoursObj.core.primary}>
                                    {i18n.t(
                                        'edit-members.adduser.travelling-with-me'
                                    )}
                                </Typography>
                                <Typography
                                    variant="bodySubtext"
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t(
                                        'edit-members.adduser.itinerary-matches'
                                    )}
                                </Typography>
                            </Stack>
                        }
                        sx={{
                            paddingBottom: '16px',
                        }}
                    />
                    <FormControlLabel
                        id="shareSomeitineraryOption"
                        value={SharingPreference.some}
                        control={<Radio />}
                        label={
                            <Stack>
                                <Typography color={ColoursObj.core.primary}>
                                    {i18n.t(
                                        'edit-members.adduser.itinerary-shares-some'
                                    )}
                                </Typography>
                                <Typography
                                    variant="bodySubtext"
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t(
                                        'edit-members.adduser.itinerary-matches-some'
                                    )}
                                </Typography>
                            </Stack>
                        }
                        sx={{
                            paddingBottom: '16px',
                        }}
                    />

                    <VerticalExpander
                        open={
                            invite.sharingPreference === SharingPreference.some
                        }
                    >
                        <FormGroup
                            onChange={(event) => {
                                const key = (event.target as HTMLInputElement)
                                    .value
                                if (sectionsToUpdate.includes(Number(key))) {
                                    setSectionsToUpdate(
                                        sectionsToUpdate.filter((element) => {
                                            return element !== Number(key)
                                        })
                                    )
                                } else {
                                    setSectionsToUpdate(
                                        sectionsToUpdate.concat([Number(key)])
                                    )
                                }
                            }}
                            sx={{
                                paddingLeft: '32px',
                                paddingBottom: '16px',
                            }}
                        >
                            {sections.slice(1).map((sectionKey, index) => {
                                return (
                                    <FormControlLabel
                                        color={ColoursObj.core.primary}
                                        id={sectionKey.name}
                                        key={index}
                                        label={
                                            <Typography
                                                color={ColoursObj.core.primary}
                                            >
                                                {i18n.t(
                                                    `sections.${sectionKey.name
                                                        .toLocaleLowerCase()
                                                        .replace(' ', '_')}`,
                                                    { count: 0 }
                                                )}
                                            </Typography>
                                        }
                                        control={
                                            <Checkbox
                                                checked={sectionsToUpdate.includes(
                                                    sectionKey.id
                                                )}
                                                value={sectionKey.id}
                                            />
                                        }
                                    />
                                )
                            })}
                        </FormGroup>
                    </VerticalExpander>

                    <FormControlLabel
                        id="ownPlansOption"
                        value={SharingPreference.none}
                        control={<Radio />}
                        label={
                            <Stack>
                                <Typography color={ColoursObj.core.primary}>
                                    {i18n.t('edit-members.adduser.own-plans')}
                                </Typography>
                                <Typography
                                    variant="bodySubtext"
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t(
                                        'edit-members.adduser.itinerary-doesnt-match'
                                    )}
                                </Typography>
                            </Stack>
                        }
                    />
                </RadioGroup>
            </Stack>
        </>
    )

    const sendButton = (
        <Button
            id="sendInviteButton"
            disabled={
                invite.textToBeParsed === '' ||
                invite.emails.length === 0 ||
                (invite.sharingPreference === 'some' &&
                    sectionsToUpdate.length === 0)
            }
            onClick={() => {
                if (validate()) {
                    sendInvite()
                }
            }}
        >
            {i18n.t('edit-members.adduser.send-invite')}
        </Button>
    )

    const confirmationDialog = (
        <>
            {lastEmailSent !== undefined && (
                <Stack
                    sx={{
                        backgroundColor: ColoursObj.backgrounds.secondary,
                    }}
                    padding="16px"
                    marginBottom="16px"
                    gap="8px"
                    bgcolor={ColoursObj.shades.shade4}
                    borderRadius="16px"
                >
                    <Stack direction="row" gap="8px">
                        <Tick fill={ColoursObj.states.success} />
                        <Typography
                            variant="bodyBold"
                            maxWidth={'Calc(100% - 32px)'}
                            overflow={'hidden'}
                            textOverflow={'ellipsis'}
                            id={'Invite sent to ' + lastEmailSent.toLowerCase()}
                        >
                            {i18n.t('edit-members.adduser.invite-sent', {
                                email: lastEmailSent.toLowerCase(),
                            })}
                        </Typography>
                    </Stack>

                    <Typography
                        variant="bodySubtext"
                        id="inviteWillReceiveText"
                    >
                        {i18n.t('edit-members.adduser.invite-sent-text')}
                    </Typography>
                </Stack>
            )}
        </>
    )

    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={i18n.t(
                    'edit-members.adduser.add-team-member'
                )}
                navigationBarSubtitle={EventTitle()}
                backButtonAction={() => {
                    window.goBack()
                }}
            >
                <Stack
                    padding="16px"
                    height="100%"
                    alignContent="space-between"
                    paddingBottom="30%"
                    overflow="scroll"
                    gap="24px"
                >
                    <Stack>{sharingPreferencePicker}</Stack>
                    <TextInputField
                        id="emailInput"
                        required
                        fieldLabel={i18n.t('general.email')}
                        error={Object.keys(errorMessages).includes(
                            ErrorMessageKey.email
                        )}
                        style={{ color: 'red' }}
                        helperText={errorMessages[ErrorMessageKey.email]}
                        value={invite.textToBeParsed}
                        onChange={(event) => {
                            /// Parsing emails...
                            let emailList = invite.emails
                            try {
                                const tmpEmailList =
                                    event.currentTarget.value.replaceAll(
                                        ' ',
                                        ''
                                    )
                                emailList = tmpEmailList.split(',')
                            } catch (error) {
                                /// could not parse.
                                spErrorTags(error, ['ERROR', 'EmailInvites'])
                            }

                            setInvite({
                                ...invite,
                                textToBeParsed: event.currentTarget.value,
                                emails: emailList,
                            })
                        }}
                        type="email"
                    />
                    <Stack>
                        {confirmationDialog}
                        {sendButton}
                    </Stack>
                    <Stack
                        justifyContent={'center'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        gap={'16px'}
                    >
                        <Typography color={ColoursObj.core.primary}>
                            {i18n.t('general.or')}
                        </Typography>
                    </Stack>

                    <Stack
                        sx={{
                            backgroundColor: ColoursObj.backgrounds.secondary,
                            borderRadius: '8px',
                            padding: '16px',
                            color: ColoursObj.core.primary,
                        }}
                        gap={'16px'}
                    >
                        <Stack
                            justifyContent={'space-between'}
                            flexDirection={'row'}
                            alignItems={'center'}
                            gap={'16px'}
                        >
                            <Typography
                                id="shareLinkText"
                                variant={'bodyRegular'}
                            >
                                {linkText}
                            </Typography>
                        </Stack>

                        <MainButton
                            id="shareLinkButton"
                            text={i18n.t('edit-members.adduser.copyShareLink')}
                            onClick={handleOnClick}
                        />

                        {showQRCode && (
                            <Stack>
                                <div
                                    style={{
                                        height: 'auto',
                                        margin: '0 auto',
                                        maxWidth: 256,
                                        width: '100%',
                                    }}
                                >
                                    <QRCode
                                        id="shareLinkQRCode"
                                        size={256}
                                        style={{
                                            height: 'auto',
                                            maxWidth: '100%',
                                            width: '100%',
                                        }}
                                        level="Q"
                                        value={itinSharecode}
                                        viewBox={`0 0 256 256`}
                                    />
                                </div>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </NavigationView>
        </>
    )
}
